import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, FormLabel, Modal, Stack } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AvatarEditor from 'react-avatar-editor';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useUpdateUserProfileMutation } from '@/features/user/userApi';
import { STORAGE_PURPOSES, userStoragePath } from '@/utils/storagePaths';

export const ProfileAvatarEditor = ({
	userId,
	email,
	imageUrl = '',
	disabled = false,
	size = 200,
	onImageUrlChange,
	onUploadingChange,
}) => {
	const dispatch = useDispatch();
	const editorRef = useRef(null);
	const [previewUrl, setPreviewUrl] = useState(imageUrl || '');
	const [isImageUploading, setIsImageUploading] = useState(false);
	const [editorOpen, setEditorOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [scale, setScale] = useState(1);
	const [updateUserProfile] = useUpdateUserProfileMutation();

	useEffect(() => {
		setPreviewUrl(imageUrl ?? '');
	}, [imageUrl]);

	const setUploading = (nextValue) => {
		setIsImageUploading(nextValue);
		onUploadingChange?.(nextValue);
	};

	const handleImageUpload = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const fileExtension = file.name.split('.').pop().toLowerCase();
		if (fileExtension !== 'jpg' && fileExtension !== 'jpeg' && fileExtension !== 'png') {
			dispatch(showToast({ message: 'Please upload a .jpg or .png file' }));
			return;
		}

		setSelectedImage(file);
		setScale(1);
		setEditorOpen(true);
	};

	const handleSaveCrop = () => {
		if (!editorRef.current || !userId || !email) return;

		const canvas = editorRef.current.getImageScaledToCanvas();
		canvas.toBlob(
			async (blob) => {
				if (!blob) return;
				try {
					setUploading(true);
					setEditorOpen(false);

					const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
					setPreviewUrl(dataUrl);

					const storage = getStorage();
					const storageRef = ref(
						storage,
						userStoragePath(userId, STORAGE_PURPOSES.profile, 'profile.jpg'),
					);
					await uploadBytes(storageRef, blob);

					const downloadUrl = await getDownloadURL(storageRef);
					setPreviewUrl(downloadUrl);
					onImageUrlChange?.(downloadUrl);
					await updateUserProfile({
						email,
						properties: { imageUrl: downloadUrl },
					}).unwrap();

					dispatch(showToast({ message: 'Image uploaded successfully!' }));
				} catch (err) {
					console.error('Error uploading image:', err);
					dispatch(showToast({ message: 'Error uploading image' }));
				} finally {
					setUploading(false);
				}
			},
			'image/jpeg',
			0.9,
		);
	};

	return (
		<>
			<Box
				sx={{
					position: 'relative',
					width: size,
					height: size,
					...(!disabled && { cursor: 'pointer' }),
				}}
			>
				<label
					style={{
						position: 'relative',
						display: 'block',
						width: '100%',
						height: '100%',
						pointerEvents: disabled ? 'none' : 'auto',
					}}
				>
					<Avatar
						src={previewUrl || ''}
						alt='Profile Picture'
						sx={{
							width: '100%',
							height: '100%',
							backgroundColor: '#999999',
							boxShadow: 2,
						}}
					>
						{!previewUrl && 'Logo'}
					</Avatar>

					{!disabled && !isImageUploading && (
						<input
							type='file'
							hidden
							accept='image/png, image/jpeg'
							onChange={handleImageUpload}
						/>
					)}

					{!disabled && (
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'backgroundColor 0.3s ease',
								...(!isImageUploading && { cursor: 'pointer' }),
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.52)',
								},
								color: 'white',
							}}
						>
							{isImageUploading ? (
								<CircularProgress color='inherit' sx={{ opacity: 1, zIndex: 999 }} />
							) : (
								<Box
									display='flex'
									justifyContent='center'
									alignItems='center'
									sx={{
										width: '100%',
										height: '100%',
										opacity: 0,
										transition: 'opacity 0.3s ease',
										'&:hover': {
											opacity: 1,
										},
									}}
								>
									<CloudUploadIcon
										fontSize={size >= 120 ? 'large' : 'medium'}
										sx={{ color: 'white' }}
									/>
								</Box>
							)}
						</Box>
					)}
				</label>
			</Box>

			<Modal
				open={editorOpen}
				onClose={() => setEditorOpen(false)}
				aria-labelledby='avatar-editor-modal'
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 400,
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						outline: 'none',
					}}
				>
					<AvatarEditor
						ref={editorRef}
						image={selectedImage}
						width={400}
						height={400}
						border={50}
						scale={scale}
						color={[255, 255, 255, 0.6]}
						style={{ width: '100%', height: 'auto' }}
					/>
					<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
						<FormLabel>Zoom</FormLabel>
						<input
							type='range'
							min='1'
							max='3'
							step='0.1'
							value={scale}
							onChange={(e) => setScale(parseFloat(e.target.value))}
							style={{ width: '100%' }}
						/>
						<Stack direction='row' spacing={2} justifyContent='flex-end'>
							<Button variant='outlined' onClick={() => setEditorOpen(false)}>
								Cancel
							</Button>
							<Button variant='contained' onClick={handleSaveCrop}>
								Save
							</Button>
						</Stack>
					</Box>
				</Box>
			</Modal>
		</>
	);
};

export default ProfileAvatarEditor;
